import React, { useMemo, useState } from 'react';
import BulkSummaryHeader from 'src/components/BulkOperations/_Shared/BulkSummaryHeader/BulkSummaryHeader';
import TabSwitch, {
  TabSwitchTab,
} from 'src/components/TabSwitch/TabSwitch';
import { ApplyResult } from 'src/components/ContactsTable/EmailSelfServiceDialogue/CustomHooks/useEmailSelfServiceOrchestrator';
import { StagedEntry } from 'src/components/ContactsTable/EmailSelfServiceDialogue/CustomHooks/useEmailSelfServiceFormState/useEmailSelfServiceFormState';
import Section from 'src/components/ContactsTable/EmailSelfServiceDialogue/StepConfirmation/Section/Section';
import './step-confirmation.scss';

interface StepConfirmationProps {
  result: ApplyResult | null;
  nowAllowedEntries: StagedEntry[];
  nowDisallowedEntries: StagedEntry[];
  unchangedEntries: StagedEntry[];
}

type ChangedTabId = 'changed';
type UnchangedTabId = 'unchanged';
type StepConfirmationTabId = ChangedTabId | UnchangedTabId;

const TAB_ID_CHANGED: ChangedTabId = 'changed';
const TAB_ID_UNCHANGED: UnchangedTabId = 'unchanged';

interface ChangesTabConfig {
  variant: 'allow' | 'disallow';
  heading: string;
  entries: StagedEntry[];
}

/* By construction the user can only ever apply one direction in a single
 * pass (the bulk toggle in step 2 is allow-or-disallow), so exactly one of
 * these arrays is populated. Default to the allow side if both are empty
 * (degenerate — shouldn't reach step 3 without changes). */
const resolveChangesTabConfig = (
  nowAllowedEntries: StagedEntry[],
  nowDisallowedEntries: StagedEntry[]
): ChangesTabConfig => {
  if (nowDisallowedEntries.length > 0) {
    return {
      variant: 'disallow',
      heading: 'No longer allowed to change email',
      entries: nowDisallowedEntries,
    };
  }

  return {
    variant: 'allow',
    heading: 'Now allowed to change email',
    entries: nowAllowedEntries,
  };
};

const StepConfirmation = ({
  result,
  nowAllowedEntries,
  nowDisallowedEntries,
  unchangedEntries,
}: StepConfirmationProps) => {
  const [activeTabId, setActiveTabId] = useState<StepConfirmationTabId>(
    TAB_ID_CHANGED
  );

  const totalUpdated: number = result?.totalUpdated ?? 0;

  const headline: string =
    totalUpdated === 1
      ? '1 contact updated'
      : `${totalUpdated} contacts updated`;

  const changesTab: ChangesTabConfig = useMemo(
    () => resolveChangesTabConfig(nowAllowedEntries, nowDisallowedEntries),
    [nowAllowedEntries, nowDisallowedEntries]
  );

  const hasUnchanged: boolean = unchangedEntries.length > 0;

  const hasAnyEntries: boolean =
    changesTab.entries.length > 0 || hasUnchanged;

  const tabs: TabSwitchTab[] = useMemo(() => {
    const built: TabSwitchTab[] = [
      {
        id: TAB_ID_CHANGED,
        label: changesTab.heading,
        count: changesTab.entries.length,
      },
    ];

    if (hasUnchanged) {
      built.push({
        id: TAB_ID_UNCHANGED,
        label: 'Unchanged',
        count: unchangedEntries.length,
      });
    }

    return built;
  }, [changesTab, hasUnchanged, unchangedEntries.length]);

  const showTabSwitch: boolean = tabs.length > 1;

  return (
    <div className="sc-step">
      <BulkSummaryHeader
        title={headline}
        subtitle="Email self-service settings were saved successfully."
        centered
      />
      {hasAnyEntries && (
        <div className="sc-sections">
          {showTabSwitch && (
            <div className="sc-tab-switch-row">
              <TabSwitch
                tabs={tabs}
                activeTabId={activeTabId}
                onTabChange={(id: string) =>
                  setActiveTabId(id as StepConfirmationTabId)
                }
              />
            </div>
          )}
          {activeTabId === TAB_ID_CHANGED && (
            <Section
              variant={changesTab.variant}
              heading={changesTab.heading}
              entries={changesTab.entries}
            />
          )}
          {activeTabId === TAB_ID_UNCHANGED && (
            <Section
              variant="unchanged"
              heading="Unchanged"
              entries={unchangedEntries}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default StepConfirmation;
